import type {
  SubmitFeedbackRequest,
  SubmitFeedbackResponse,
} from '@seen/request-types/submitFeedback'
import { Button, Label } from '@seen/ui'
import { useMutation } from '@tanstack/react-query'
import type { JSX } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { Checkbox } from '#src/components/core/Checkbox.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogDescription } from '#src/components/core/dialog/DialogDescription.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { SubmitButton } from '#src/components/core/form/SubmitButton.tsx'
import { Textarea } from '#src/components/core/Textarea.tsx'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { useFeedbackTelemetry } from '#src/hooks/useFeedbackTelemetry.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'

const FEEDBACK_PATH = '/feedback/submit'

// Direct link to the #proc-feedback-ehr-phi Slack channel, kept as a manual fallback if
// in-product submission fails.
const FEEDBACK_SLACK_URL = 'slack://channel?team=T01JRM6QHPW&id=C07V6Q9BGS2'

const formSchema = z.object({
  body: z
    .string()
    .transform((v) => v.trim())
    .refine((v) => v.length > 0, { message: 'Feedback required' })
    .refine((v) => v.length <= 4000, {
      message: 'Feedback must be 4,000 characters or fewer',
    }),
  notifyReporter: z.boolean(),
})

type FeedbackFormValues = z.infer<typeof formSchema>

export function FeedbackDialog(): JSX.Element {
  const open = usePortalStore((state) => state.feedbackDialogOpen)
  const setOpen = usePortalStore((state) => state.setFeedbackDialogOpen)
  const draft = usePortalStore((state) => state.feedbackDraft)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogDisplay>
        <FeedbackDialogContent
          key={draft}
          draft={draft}
          onClose={() => setOpen(false)}
        />
      </DialogDisplay>
    </Dialog>
  )
}

function FeedbackDialogContent({
  draft,
  onClose,
}: {
  draft: string
  onClose: () => void
}): JSX.Element {
  const { seenApi } = useSeenApi()
  const captureTelemetry = useFeedbackTelemetry()

  const form = useForm({
    schema: formSchema,
    defaultValues: { body: draft, notifyReporter: true },
  })

  const submit = useMutation({
    mutationFn: async (values: FeedbackFormValues) => {
      // Session + diagnostics are always attached so every report is
      // reproducible without asking the user to opt in.
      const payload: SubmitFeedbackRequest = {
        body: values.body,
        context: captureTelemetry(),
        notifyReporter: values.notifyReporter,
      }
      const { data } = await seenApi.post<SubmitFeedbackResponse>(
        FEEDBACK_PATH,
        payload
      )
      return data
    },
    onSuccess: (data) => {
      // Prefer a direct link to the posted message; fall back to opening the
      // #proc-feedback-ehr-phi channel when the backend couldn't resolve a permalink.
      const hasDirectLink = Boolean(data.slackUrl)
      toast.success(
        <div className="flex flex-col gap-1">
          <span>Feedback sent — thank you!</span>
          <a
            href={data.slackUrl ?? FEEDBACK_SLACK_URL}
            target="_blank"
            rel="noreferrer"
            className="text-xs underline hover:text-foreground"
          >
            {hasDirectLink ? 'View in Slack' : 'View in #proc-feedback-ehr-phi'}
          </a>
        </div>
      )
      form.reset()
      onClose()
    },
    onError: (error) => {
      console.error('[feedback] failed to submit', error)
      toast.error('Could not send feedback. Please try again.')
    },
  })

  const onSubmit = form.handleSubmit((values) => {
    submit.mutate(values)
  })

  return (
    <Form onSubmit={onSubmit}>
      <DialogHeader asChild>
        <DialogTitle>Send feedback</DialogTitle>
      </DialogHeader>
      <DialogContent className="flex flex-col gap-4 pt-2">
        <DialogDescription>
          Report a bug or share an idea. We'll include your current page and
          session so we can dig in.
        </DialogDescription>

        <FormField
          control={form.control}
          name="body"
          render={({ field }) => (
            <Textarea
              value={field.value}
              onChange={field.onChange}
              placeholder="What's working, what isn't, or what you'd like to see…"
              autoFocus
              rows={5}
            />
          )}
        />

        <p className="text-xs text-muted-foreground">
          Having trouble submitting? Reach us directly in{' '}
          <a
            href={FEEDBACK_SLACK_URL}
            className="underline hover:text-foreground"
          >
            #proc-feedback-ehr-phi
          </a>
          .
        </p>
      </DialogContent>
      <DialogFooter className="sm:items-center sm:justify-between">
        <FormField
          control={form.control}
          name="notifyReporter"
          render={({ field: { value, onChange, ...rest } }) => (
            <div className="flex items-center gap-2">
              <Checkbox
                {...rest}
                value={value}
                onChange={onChange}
                className="data-[state=checked]:border-muted-foreground data-[state=checked]:bg-muted-foreground"
              />
              <Label
                htmlFor={rest.id}
                unstyled
                className="cursor-pointer text-xs text-muted-foreground"
              >
                Notify me in Slack
              </Label>
            </div>
          )}
        />
        <div className="flex gap-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <SubmitButton submitting={submit.isPending}>Send</SubmitButton>
        </div>
      </DialogFooter>
    </Form>
  )
}
